import { computed, ref } from "vue";
import { decodeJwt, type JwtClaims } from "@/lib/jwt";
import { randomVerifier, sha256Challenge } from "@/lib/pkce";
import { getRuntimeConfig } from "@/lib/runtime-config";
import { local, session } from "@/lib/storage";

const RT_KEY = "refresh_token";
const PKCE_KEY = "pkce_verifier";
const STATE_KEY = "oauth_state";

const idToken = ref<string | null>(null);
const idTokenExp = ref<number>(0);
const claims = ref<JwtClaims | null>(null);

export function useAuth() {
  const isSignedIn = computed(() => !!idToken.value);
  const email = computed(() => (claims.value?.email as string | undefined) ?? "");
  const groups = computed<string[]>(() => {
    const raw = claims.value?.["cognito:groups"];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string" && raw.length > 0) return raw.split(/[\s,]+/);
    return [];
  });
  /** Authoritative role lives on the user's `custom:role` Cognito attribute,
   * which flows into the ID token as a claim. Default is `viewer` (see all
   * projects, no admin powers) for federated users without the attribute
   * set yet. Legacy `cognito:groups` checked as a fallback for admins
   * during the migration window. */
  const role = computed<"admin" | "viewer" | "user">(() => {
    const claimed = claims.value?.["custom:role"];
    if (claimed === "admin") return "admin";
    if (claimed === "viewer") return "viewer";
    if (claimed === "user") return "user";
    if (groups.value.includes("scorecard-admin")) return "admin";
    return "viewer";
  });
  /** Per-user project allowlist. Only meaningful for role=user; admin and
   * viewer always see everything. `["*"]` means see-everything; missing
   * or blank means the same. */
  const projects = computed<string[]>(() => {
    const raw = claims.value?.["custom:projects"];
    if (typeof raw !== "string" || raw.trim() === "" || raw.trim() === "*") return ["*"];
    const keys = raw.split(",").map((k) => k.trim()).filter(Boolean);
    return keys.length === 0 ? ["*"] : keys;
  });
  const isAdmin = computed(() => role.value === "admin");
  const canSeeProject = (key: string): boolean => {
    if (role.value === "admin" || role.value === "viewer") return true;
    const list = projects.value;
    return list.includes("*") || list.includes(key);
  };

  /** Federated IdPs registered on the Cognito User Pool. The string must
   * match the Cognito IdP name exactly (case-sensitive). Add new providers
   * here as they're configured in Cognito; the SPA-side wiring is just a
   * button + this union. */
  type IdentityProvider = "Google" | "Zoho";

  async function buildSignInUrl(idp: IdentityProvider = "Google"): Promise<string> {
    const cfg = getRuntimeConfig();
    const verifier = randomVerifier();
    const challenge = await sha256Challenge(verifier);
    const state = randomVerifier(16);
    session.set(PKCE_KEY, verifier);
    session.set(STATE_KEY, state);

    const u = new URL(`${cfg.cognitoDomain}/oauth2/authorize`);
    u.searchParams.set("identity_provider", idp);
    u.searchParams.set("response_type", "code");
    u.searchParams.set("client_id", cfg.cognitoClientId);
    u.searchParams.set("scope", "openid email profile");
    u.searchParams.set("redirect_uri", cfg.cognitoRedirectUri);
    u.searchParams.set("code_challenge", challenge);
    u.searchParams.set("code_challenge_method", "S256");
    u.searchParams.set("state", state);
    return u.toString();
  }

  async function startSignIn(idp: IdentityProvider = "Google"): Promise<void> {
    window.location.assign(await buildSignInUrl(idp));
  }

  function setTokens(tok: { id_token: string; refresh_token?: string }, keepRT = false): void {
    idToken.value = tok.id_token;
    claims.value = decodeJwt(tok.id_token);
    idTokenExp.value =
      typeof claims.value?.exp === "number" ? claims.value.exp : Math.floor(Date.now() / 1000) + 3500;
    if (!keepRT && tok.refresh_token) {
      local.set(RT_KEY, tok.refresh_token);
    }
  }

  function clearTokens(): void {
    idToken.value = null;
    idTokenExp.value = 0;
    claims.value = null;
    local.remove(RT_KEY);
  }

  async function exchangeCode(code: string): Promise<void> {
    const cfg = getRuntimeConfig();
    const verifier = session.get(PKCE_KEY);
    console.log("[exchangeCode] start", { hasVerifier: !!verifier, codeLen: code.length, redirect: cfg.cognitoRedirectUri });
    if (!verifier) throw new Error("PKCE verifier missing — sign-in flow not started in this tab");
    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cfg.cognitoClientId,
      code,
      redirect_uri: cfg.cognitoRedirectUri,
      code_verifier: verifier,
    });
    const res = await fetch(`${cfg.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => "<no body>");
      console.error("[exchangeCode] /oauth2/token NOT OK", res.status, txt);
      throw new Error(`Token exchange failed (${res.status})`);
    }
    const tok = (await res.json()) as { id_token: string; refresh_token?: string };
    console.log("[exchangeCode] /oauth2/token OK", {
      hasIdToken: !!tok.id_token,
      idTokenLen: tok.id_token?.length,
      hasRefreshToken: !!tok.refresh_token,
    });
    setTokens(tok);
    console.log("[exchangeCode] after setTokens", {
      idTokenSet: !!idToken.value,
      claimsEmail: claims.value?.email,
      claimsSub: claims.value?.sub,
      claimsExp: claims.value?.exp,
    });
    session.remove(PKCE_KEY);
    session.remove(STATE_KEY);
  }

  async function refresh(): Promise<void> {
    const cfg = getRuntimeConfig();
    const rt = local.get(RT_KEY);
    if (!rt) throw new Error("No refresh token stored");
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: cfg.cognitoClientId,
      refresh_token: rt,
    });
    const res = await fetch(`${cfg.cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    });
    if (!res.ok) {
      local.remove(RT_KEY);
      throw new Error(`Refresh failed (${res.status})`);
    }
    const tok = (await res.json()) as { id_token: string; refresh_token?: string };
    setTokens(tok, /*keepRT*/ true);
  }

  async function ensureFreshToken(): Promise<string | null> {
    if (!idToken.value) return null;
    const msToExpiry = idTokenExp.value * 1000 - Date.now();
    if (msToExpiry < 60_000) {
      try {
        await refresh();
      } catch {
        return null;
      }
    }
    return idToken.value;
  }

  function signOut(): void {
    const cfg = getRuntimeConfig();
    clearTokens();
    const u = new URL(`${cfg.cognitoDomain}/logout`);
    u.searchParams.set("client_id", cfg.cognitoClientId);
    u.searchParams.set("logout_uri", cfg.cognitoRedirectUri);
    window.location.assign(u.toString());
  }

  return {
    // state
    idToken,
    isSignedIn,
    email,
    groups,
    role,
    isAdmin,
    projects,
    canSeeProject,
    claims,
    idTokenExp,
    // actions
    buildSignInUrl,
    startSignIn,
    setTokens,
    clearTokens,
    exchangeCode,
    refresh,
    ensureFreshToken,
    signOut,
  };
}
